import { useState, useCallback } from 'react';
import {
  EXPERIMENT_MIN_SAMPLE_SIZE,
  EXPERIMENT_DEFAULT_STATISTICAL_SIGNIFICANCE,
} from '../../../../config/constants';

/**
 * Custom hook for managing experiment creation form state.
 *
 * @param {Object} selectedTemplate - Selected template object
 * @returns {Object} Form state and handlers
 */
export function useExperimentCreationForm(selectedTemplate) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template_id: '',
    treatments: [],
    traffic_allocation: 'equal_split',
    success_metrics: [],
    minimum_sample_size: EXPERIMENT_MIN_SAMPLE_SIZE,
    statistical_significance: EXPERIMENT_DEFAULT_STATISTICAL_SIGNIFICANCE,
    target_audience: null,
    exclusion_criteria: null,
    metadata: {},
  });

  const [errors, setErrors] = useState({});
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const toggleDescriptionExpansion = useCallback(() => {
    setIsDescriptionExpanded(prev => !prev);
  }, []);

  const shouldShowMoreButton = useCallback(description => {
    if (!description) return false;
    const wordCount = description.trim().split(/\s+/).length;
    return wordCount > 30;
  }, []);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    setErrors(prev => {
      if (prev[field]) {
        return {
          ...prev,
          [field]: null,
        };
      }
      return prev;
    });
  }, []);

  const handleTreatmentChange = useCallback((index, field, value) => {
    setFormData(prev => {
      const newTreatments = [...prev.treatments];
      newTreatments[index] = {
        ...newTreatments[index],
        [field]: value,
      };
      return {
        ...prev,
        treatments: newTreatments,
      };
    });
  }, []);

  const removeTreatment = useCallback(index => {
    setFormData(prev => ({
      ...prev,
      treatments: prev.treatments.filter((_, i) => i !== index),
    }));
  }, []);

  const initializeForm = useCallback(() => {
    if (selectedTemplate) {
      setFormData(prev => ({
        ...prev,
        template_id: selectedTemplate.id,
        name: `${selectedTemplate.name} A/B Test`,
        treatments: [
          {
            treatment_id: `control_${Date.now()}`,
            name: 'Control',
            description: 'Original template version',
            treatment_type: 'control',
            template_version: parseInt(selectedTemplate.version) || 1,
            template_modifications: {},
            hypothesis: 'Control treatment for baseline comparison',
            tags: ['control', 'baseline'],
          },
        ],
      }));
    }
  }, [selectedTemplate]);

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    isDescriptionExpanded,
    toggleDescriptionExpansion,
    shouldShowMoreButton,
    handleInputChange,
    handleTreatmentChange,
    removeTreatment,
    initializeForm,
  };
}
